import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import {
  Box,
  Stack,
  HStack,
  Button,
  IconButton,
  useToast,
} from "@chakra-ui/react";
import { ViewIcon, ViewOffIcon } from "@chakra-ui/icons";
import Form from "@rjsf/chakra-ui";
import validator from "@rjsf/validator-ajv8";
import ImageWidget from "../../CommonComponents/ImageWidget";
import SelectWidget from "../../CommonComponents/SelectWidget";
import ImagePreview from "../../components/ImagePreview";
import FormLoader from "../../CommonComponents/FormLoader";
import {
  getDeletedValues,
  reverseTransformData,
  transformData,
} from "../../util/helper";
import {
  getResearchCategoryData,
  updateResearchCategoriesData,
  getRcTypeSuggestions,
} from "../../services/ResearchCategories";
import { useSelector } from "react-redux";
import { handleApiError, showSuccessToast } from "../../util/errorHandler";

const RCPrimaryForm = () => {
  const { alias } = useParams();
  const [basicFieldData, setBasicFieldData] = useState({});
  const [quickSave, setQuickSave] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showImagePreview, setShowImagePreview] = useState(false);
  const [leftFormData, setLeftFormData] = useState({});
  const [rightFormData, setRightFormData] = useState({});
  const [combinedFormData, setCombinedFormData] = useState({});
  const previewEnabled = useSelector((state) => state.preview.isEnabled);
  const toast = useToast();

  // Split data for two-column layout
  const splitFormData = (data = {}) => {
    const leftFields = [
      "_id",
      "alias",
      "name",
      "sub_name",
      "value",
      "main_image",
      "preview_main_image",
    ];
    const rightFields = [
      "description",
      "images",
      "relevance_to_self_discovery_journey",
      "research_category_as_in_website",
    ];

    const leftData = {};
    const rightData = {};

    leftFields.forEach((field) => {
      if (data.hasOwnProperty(field)) {
        leftData[field] = data[field];
      }
    });

    rightFields.forEach((field) => {
      if (data.hasOwnProperty(field)) {
        rightData[field] = data[field];
      }
    });

    return { leftData, rightData };
  };

  // Combine data from two forms
  const combineFormData = (leftData, rightData) => {
    return { ...leftData, ...rightData };
  };

  // Left column schema
  const leftColumnSchema = {
    title: "Research Category Fields",
    type: "object",
    properties: {
      _id: { type: "string" },
      alias: {
        type: "string",
        title: "Alias",
        default: "",
      },
      name: {
        type: "string",
        title: "Name",
        default: "",
      },
      sub_name: {
        type: "string",
        title: "Sub Name",
        default: "",
      },
      value: {
        type: "string",
        title: "Value",
        default: "",
      },
      main_image: {
        type: "string",
        title: "Main Image",
        default: "",
      },
      preview_main_image: {
        type: "string",
        title: "Image",
      },
    },
    required: ["name", "alias"],
  };

  // Right column schema
  const rightColumnSchema = {
    title: "",
    type: "object",
    properties: {
      description: {
        type: "string",
        title: "Description",
      },
      images: {
        type: "array",
        title: "Images",
        items: {
          type: "object",
          properties: {
            image_alias: {
              type: "string",
              title: "Image Alias",
              default: "",
            },
            image: {
              type: "string",
              title: "Image",
              default: "",
            },
          },
        },
      },
      relevance_to_self_discovery_journey: {
        type: "string",
        title: "Relevance To Self Discovery Journey",
        default: "",
      },
      research_category_as_in_website: {
        type: "string",
        title: "Research Category As In Website",
        default: "",
      },
    },
  };

  // Combined schema for preview mode
  const combinedSchema = {
    title: "Research Category Fields",
    type: "object",
    properties: {
      _id: { type: "string" },
      alias: {
        type: "string",
        title: "Alias",
        default: "",
      },
      name: {
        type: "string",
        title: "Name",
        default: "",
      },
      sub_name: {
        type: "string",
        title: "Sub Name",
        default: "",
      },
      value: {
        type: "string",
        title: "Value",
        default: "",
      },
      main_image: {
        type: "string",
        title: "Main Image",
        default: "",
      },
      preview_main_image: {
        type: "string",
        title: "Image",
      },
      description: {
        type: "string",
        title: "Description",
      },
      images: {
        type: "array",
        title: "Images",
        items: {
          type: "object",
          properties: {
            image_alias: {
              type: "string",
              title: "Image Alias",
              default: "",
            },
            image: {
              type: "string",
              title: "Image",
              default: "",
            },
          },
        },
      },
      relevance_to_self_discovery_journey: {
        type: "string",
        title: "Relevance To Self Discovery Journey",
        default: "",
      },
      research_category_as_in_website: {
        type: "string",
        title: "Research Category As In Website",
        default: "",
      },
    },
    required: ["name", "alias"],
  };

  // Left column UI schema
  const leftColumnUiSchema = {
    "ui:submitButtonOptions": { norender: true },
    _id: { "ui:widget": "hidden" },
    "ui:style": {
      fontSize: "14px",
    },
    preview_main_image: {
      "ui:widget": ImageWidget,
    },
    sub_name: {
      "ui:emptyValue": "",
    },
    main_image: {
      "ui:emptyValue": "",
    },
    name: {
      "ui:emptyValue": "",
    },
    value: {
      "ui:emptyValue": "",
    },
    alias: {
      "ui:readonly": true,
    },
  };

  // Right column UI schema
  const rightColumnUiSchema = {
    "ui:submitButtonOptions": { norender: true },
    "ui:style": {
      fontSize: "14px",
    },
    images: {
      items: {
        "ui:emptyValue": [],
        image: {
          "ui:widget": ImageWidget,
        },
      },
    },
    description: {
      "ui:widget": "textarea",
    },
    research_category_as_in_website: {
      "ui:emptyValue": "",
    },
    relevance_to_self_discovery_journey: {
      "ui:emptyValue": "",
      "ui:widget": "selectdropdown",
    },
  };

  // Combined UI schema for preview mode
  const combinedUiSchema = {
    "ui:submitButtonOptions": { norender: true },
    _id: { "ui:widget": "hidden" },
    "ui:style": {
      fontSize: "14px",
    },
    images: {
      items: {
        "ui:emptyValue": [],
        image: {
          "ui:widget": ImageWidget,
        },
      },
    },
    preview_main_image: {
      "ui:widget": ImageWidget,
    },
    description: {
      "ui:widget": "textarea",
    },
    sub_name: {
      "ui:emptyValue": "",
    },
    research_category_as_in_website: {
      "ui:emptyValue": "",
    },
    relevance_to_self_discovery_journey: {
      "ui:emptyValue": "",
      "ui:widget": "selectdropdown",
    },
    main_image: {
      "ui:emptyValue": "",
    },
    name: {
      "ui:emptyValue": "",
    },
    value: {
      "ui:emptyValue": "",
    },
    alias: {
      "ui:readonly": true,
    },
  };

  // Form change handlers
  const handleLeftFormChange = (data) => {
    setLeftFormData(data.formData);
  };

  const handleRightFormChange = (data) => {
    setRightFormData(data.formData);
  };

  const handleCombinedFormChange = (data) => {
    setCombinedFormData(data.formData);
  };

  const widgets = {
    selectdropdown: (props) => (
      <SelectWidget {...props} fetchOptions={getRcTypeSuggestions} />
    ),
  };

  const dataWithImage = (data) => ({
    ...data,
    preview_main_image: data?.main_image,
  });

  // Fetch initial data
  useEffect(() => {
    const getBasicData = async () => {
      try {
        if (alias) {
          const result = await getResearchCategoryData(alias);
          if (result) {
            const updatedDataWithImage = dataWithImage(result?.data?.[0]);
            const fieldsToTransform = ["images"];

            // To Show image on form need to provide url to image field which is only used to show the image
            const data = transformData(updatedDataWithImage, fieldsToTransform);

            // Split data for two-column layout
            const { leftData, rightData } = splitFormData(data);
            setLeftFormData(leftData);
            setRightFormData(rightData);
            setCombinedFormData(data);
            setBasicFieldData(result?.data?.[0]);
            setQuickSave(false);
          }
        } else {
          // For new records, initialize empty forms
          const emptyData = {};
          const { leftData, rightData } = splitFormData(emptyData);
          setLeftFormData(leftData);
          setRightFormData(rightData);
          setCombinedFormData(emptyData);
        }
      } catch (error) {
        handleApiError(error, toast, {
          defaultErrorMessage: "Failed to fetch data",
        });
      } finally {
        setIsLoading(false);
      }
    };

    getBasicData();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [alias, quickSave]);

  // Submit updated data
  const handleSubmit = async () => {
    setIsSubmitting(true);

    try {
      let finalFormData;

      if (previewEnabled) {
        finalFormData = combinedFormData;
      } else {
        finalFormData = combineFormData(leftFormData, rightFormData);
      }

      const fieldsToTransform = ["images"];

      // Remove preview image field
      delete finalFormData.preview_main_image;

      // Remove _id from create operations
      if (!alias && finalFormData._id) {
        delete finalFormData._id;
      }

      const updatedFormData = reverseTransformData(
        finalFormData,
        fieldsToTransform,
      );
      const deletedValues = getDeletedValues(basicFieldData, updatedFormData);

      if (alias) {
        const result = await updateResearchCategoriesData(
          alias,
          updatedFormData,
          deletedValues,
        );
        if (result?.success) {
          setQuickSave((prev) => !prev);
          showSuccessToast(toast, "Data updated successfully");
        }
      }
    } catch (error) {
      handleApiError(error, toast, {
        defaultErrorMessage: "Failed to update data",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Box maxW="auto" pb="100px">
      {isLoading ? (
        <FormLoader />
      ) : (
        <Box>
          {previewEnabled ? (
            /* Single column layout for preview mode */
            <Box
              display={"flex"}
              flexDirection={"row"}
              alignItems={"flex-start"}
            >
              <Box
                flex={1}
                bg="white"
                border="1px solid"
                borderColor="gray.200"
                p={4}
                borderRadius="md"
              >
                <HStack justifyContent="space-between" mb={4}>
                  <Box fontSize="lg" fontWeight="bold">
                    Research Category Fields
                  </Box>
                  {combinedFormData?.main_image && (
                    <IconButton
                      aria-label={
                        showImagePreview
                          ? "Hide image preview"
                          : "Show image preview"
                      }
                      icon={showImagePreview ? <ViewOffIcon /> : <ViewIcon />}
                      size="sm"
                      colorScheme={showImagePreview ? "red" : "blue"}
                      onClick={() => setShowImagePreview((prev) => !prev)}
                      title={
                        showImagePreview
                          ? "Close image preview"
                          : "Open image preview"
                      }
                    />
                  )}
                </HStack>
                <Form
                  schema={combinedSchema}
                  uiSchema={combinedUiSchema}
                  formData={combinedFormData}
                  validator={validator}
                  widgets={widgets}
                  onChange={handleCombinedFormChange}
                  fontsize={"14px"}
                />
              </Box>
              {/* Third column for image preview when clicked */}
              {showImagePreview && combinedFormData?.main_image && (
                <HStack
                  sx={{ position: "sticky", top: "10px", marginLeft: "0px" }}
                >
                  <ImagePreview image={combinedFormData?.main_image} />
                </HStack>
              )}
            </Box>
          ) : (
            /* Two column layout with separate forms */
            <Box
              display={"flex"}
              flexDirection={"row"}
              alignItems={"flex-start"}
            >
              <Stack
                flexDirection="row"
                gap={4}
                alignItems="flex-start"
                flex={1}
              >
                <HStack alignItems="flex-start" flex={1}>
                  {/* Left Column */}
                  <Box
                    flex={1}
                    bg="white"
                    border="1px solid"
                    borderColor="gray.200"
                    p={4}
                    borderRadius="md"
                  >
                    <HStack justifyContent="space-between" mb={4}>
                      <Box fontSize="lg" fontWeight="bold">
                        Research Category Fields
                      </Box>
                      {leftFormData?.main_image && (
                        <IconButton
                          aria-label={
                            showImagePreview
                              ? "Hide image preview"
                              : "Show image preview"
                          }
                          icon={
                            showImagePreview ? <ViewOffIcon /> : <ViewIcon />
                          }
                          size="sm"
                          colorScheme={showImagePreview ? "red" : "blue"}
                          onClick={() => setShowImagePreview((prev) => !prev)}
                          title={
                            showImagePreview
                              ? "Close image preview"
                              : "Open image preview"
                          }
                        />
                      )}
                    </HStack>
                    <Form
                      schema={leftColumnSchema}
                      uiSchema={leftColumnUiSchema}
                      formData={leftFormData}
                      validator={validator}
                      widgets={widgets}
                      onChange={handleLeftFormChange}
                      fontsize={"14px"}
                    />
                  </Box>
                  {/* Right Column */}
                  <Box
                    flex={1}
                    bg="white"
                    border="1px solid"
                    borderColor="gray.200"
                    p={4}
                    borderRadius="md"
                  >
                    <Form
                      schema={rightColumnSchema}
                      uiSchema={rightColumnUiSchema}
                      formData={rightFormData}
                      validator={validator}
                      widgets={widgets}
                      onChange={handleRightFormChange}
                      fontsize={"14px"}
                    />
                  </Box>
                </HStack>
              </Stack>
              {/* Third column for image preview when clicked */}
              {showImagePreview && leftFormData?.main_image && (
                <HStack
                  sx={{ position: "sticky", top: "10px", marginLeft: "0px" }}
                >
                  <ImagePreview image={leftFormData?.main_image} />
                </HStack>
              )}
            </Box>
          )}
        </Box>
      )}

      {/* Fixed Save Button */}
      <Box
        position="fixed"
        bottom={0}
        left={"250px"}
        right={0}
        bg="white"
        borderTop="1px solid"
        borderColor="gray.200"
        p={4}
        zIndex={1000}
      >
        <Box maxW="auto" mx="auto" display="flex" justifyContent="center">
          <Button
            colorScheme="blue"
            onClick={handleSubmit}
            isLoading={isSubmitting}
            width="auto"
            margin="0 auto"
            textAlign="center"
          >
            {isSubmitting ? "Saving..." : "Save"}
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

export default RCPrimaryForm;
